import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import { useCart } from "../../context/CartContext";
import { uploadCustomCakeImage } from "../../utils/uploadCustomCakeImage";

import {
  Page,
  Inner,
  Title,
  Subtitle,
  EmptyBox,
  Grid,
  List,
  ItemCard,
  ItemTop,
  Img,
  Info,
  NameRow,
  Name,
  Meta,
  MetaAlert,
  InlineControls,
  GramajSelect,
  SelectField,
  Actions,
  QtyWrap,
  MobileQtyWrap,
  QtyBtn,
  QtyValue,
  PriceWrap,
  OldPrice,
  Price,
  DiscountBadge,
  RemoveIconBtn,
  Summary,
  SummaryTitle,
  SummaryText,
  SummaryCard,
  SummaryRow,
  SummaryMini,
  PrimaryBtn,
  SecondaryBtn,
  CustomSummaryBox,
  SummaryLine,
  SummaryKey,
  SummaryValue,
  EditToggleBtn,
  ExtraFields,
  FieldGroup,
  FieldGroupWide,
  FieldLabel,
  TextArea,
  HiddenFileInput,
  FileUploadRow,
  FileTrigger,
  FileName,
  FileHint,
  Divider,
  TopActions,
  BottomRow,
  HeaderTools,
} from "./CartPageStyles";

function formatDisponibilitate(termenOre) {
  const ore = Number(termenOre || 0);

  if (ore <= 0) return "Disponibil imediat";

  const zile = Math.ceil(ore / 24);

  if (zile === 1) return "Disponibil în 1 zi";
  return `Disponibil în ${zile} zile`;
}

function roundPrice(value) {
  return Math.round(Number(value || 0) * 2) / 2;
}

function formatLei(value) {
  const rounded = roundPrice(value);

  return `${rounded.toLocaleString("ro-RO", {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} lei`;
}

function isVariablePrice(item) {
  return item.tip_pret === "variabil" || item.tip_pret === "kg";
}

function isCustomCake(item) {
  return item.este_tort_personalizat === true;
}

function isBentoCake(item) {
  return (
    item.nume?.toLowerCase().includes("bento") ||
    item.categorie === "bento_cake"
  );
}

function getDiscountedUnitPrice(item) {
  const pret = Number(item.pret || 0);
  const reducereTip = item.reducere_tip || "procent";
  const reducere = Number(item.reducere_procent || 0);
  const pretRedus = Number(item.pret_redus || 0);

  if (reducereTip === "pret_nou" && pretRedus > 0 && pretRedus < pret) {
    return pretRedus;
  }

  if (reducereTip === "procent" && reducere > 0) {
    return Math.round((pret - (pret * reducere) / 100) * 2) / 2;
  }

  return pret;
}

function calcLineTotal(item) {
  const cantitate = Number(item.cantitate || 1);

  if (isVariablePrice(item)) {
    const pretPeKg = isCustomCake(item)
      ? Number(item.personalizare_pret_kg || item.pret || 0)
      : getDiscountedUnitPrice(item);

    const pretDinGramaj = (pretPeKg * Number(item.gramaj || 0)) / 1000;
    return roundPrice(pretDinGramaj * cantitate);
  }

  return roundPrice(getDiscountedUnitPrice(item) * cantitate);
}

function calcLineTotalBeforeDiscount(item) {
  const cantitate = Number(item.cantitate || 1);

  if (isVariablePrice(item)) {
    const pretPeKg = isCustomCake(item)
      ? Number(item.personalizare_pret_kg || item.pret || 0)
      : Number(item.pret || 0);

    const pretDinGramaj = (pretPeKg * Number(item.gramaj || 0)) / 1000;
    return roundPrice(pretDinGramaj * cantitate);
  }

  return roundPrice(Number(item.pret || 0) * cantitate);
}

function buildGramajOptions(item) {
  const variable = isVariablePrice(item);
  const selectable = item.gramaj_selectabil ?? variable;

  if (!variable || !selectable) return [];

  const min = Number(item.gramaj_min || 0);
  const max = Number(item.gramaj_max || 0);
  const step = Number(item.pas_gramaj || 0);

  if (!min || !max || !step) return [];

  const arr = [];
  for (let value = min; value <= max; value += step) {
    arr.push(value);
  }
  return arr;
}

function formatGramaj(g) {
  if (!g) return "";
  if (Number(g) >= 1000) return `${Number(g) / 1000} kg`;
  return `${g} g`;
}

const optiuniPersonalizare = [
  { nume: "Medovik cu zmeură", pret_kg: 150 },
  { nume: "Napoleon cu caramel și mere", pret_kg: 150 },
  { nume: "Tort cu Banană și Căpșuni", pret_kg: 140 },
  { nume: "Tort Caramel și Arahide", pret_kg: 160 },
  {
    nume: "Tort cu blat de brownie, mousse de zmeură, ganache de ciocolată neagră cu alune și Nutella",
    pret_kg: 180,
  },
  { nume: "Tort cu blat de fistic și cremă de vanilie", pret_kg: 170 },
  {
    nume: "Tort cu cremă de vanilie și cremă de caramel cu crunch",
    pret_kg: 160,
  },
  { nume: "Tort cu cremă de vanilie și inserție de ciocolată", pret_kg: 150 },
  {
    nume: "Tort cu cremă de vanilie și inserție de zmeură și mousse de ciocolată",
    pret_kg: 160,
  },
  {
    nume: "Tort cu cremă de vanilie și mini eclere umplute cu fructe și cremă de fistic",
    pret_kg: 160,
  },
  { nume: "Tort cu gust de octombrie", pret_kg: 150 },
  { nume: "Tort cu mousse de ciocolată și inserție de zmeură", pret_kg: 150 },
  { nume: "Tort cu nutella alune", pret_kg: 150 },
  { nume: "Tort cu oreo", pret_kg: 150 },
  { nume: "Tort cu oreo și arahide", pret_kg: 150 },
  { nume: "Tort cu oreo și caramel", pret_kg: 150 },
  { nume: "Tort exotic", pret_kg: 180 },
  { nume: "Tort lămâie caramel", pret_kg: 150 },
  { nume: "Tort levănțică vanilie", pret_kg: 115 },
  { nume: "Tort medovik portocală", pret_kg: 180 },
  { nume: "Tort mousse de ciocolată", pret_kg: 150 },
  { nume: "Tort plombir cu vișină", pret_kg: 165 },
  { nume: "Tort poppy peach", pret_kg: 160 },
  { nume: "Tort red velvet", pret_kg: 150 },
  { nume: "Tort red velvet cu zmeură", pret_kg: 160 },
  { nume: "Tort tropic", pret_kg: 180 },
];

export default function CartPage() {
  const MIN_ORDER_VALUE = 50;

  const navigate = useNavigate();
  const {
    items,
    totalPret,
    removeFromCart,
    updateQuantity,
    updateGramaj,
    updateMentiuni,
    updateMesajSelectat,
    updatePersonalizare,
    updateDescriereDecor,
    updatePozaModel,
  } = useCart();

  const MESSAGE_OPTIONS_BY_PRODUCT_ID = {
    1: [
      "One Month, So Loved",
      "Two Months of Cuteness",
      "Three Months, Hello Love",
      "Four Months of Smiles",
      "Five Months of Happiness",
      "Six Months, Tiny & Loved",
      "Seven Months of Wonder",
      "Eight Months, Sweet as Can Be",
      "Nine Months of Joy",
      "Ten Months of Love",
      "Eleven Months of Perfection",
      "One Magical Year",
    ],
    2: [
      "Two Years of Joy",
      "Three Years of Magic",
      "Four Years of Fun",
      "Five Years of Happiness",
      "Six Years of Wonder",
      "Seven Years of Magic",
      "Eight Years of Smiles",
      "Nine Years of Joy",
    ],
    3: [
      "Față card: Dacă ai primit asta, știi ce ai de făcut. Verso card: Un gest mic care spune exact ce trebuie. Ahava Cakes",
      "Față card: Pentru tine. Verso card: Fără alte explicații. Ahava Cakes",
      "Față card: Un semn bun. Verso card: Foarte bun. Ahava Cakes",
      "Față card:Când vrei să spui ceva. Verso card: Fără să spui nimic.",
      "Față card: Știi tu de ce. Verso card: Ahava Cakes",
      "Față card: A fost o idee bună. Verso card: Ahava Cakes",
      "Față card: Exact ce trebuia. Verso card: Ahava Cakes",
      "Față card: Data viitoare, tu. Verso card: Ahava Cakes",
    ],
    4: [
      "Just Because",
      "With Love",
      "Sweet Moments",
      "A Little Joy",
      "Pentru tine",
      "Exact ce trebuia",
      "Un gând dulce",
      "Doar pentru tine",
      "La mulți ani!",
      "O mică bucurie",
      "Un moment dulce",
    ],
    56: [
      "One Month, So Loved",
      "Two Months of Cuteness",
      "Three Months, Hello Love",
      "Four Months of Smiles",
      "Five Months of Happiness",
      "Six Months, Tiny & Loved",
      "Seven Months of Wonder",
      "Eight Months, Sweet as Can Be",
      "Nine Months of Joy",
      "Ten Months of Love",
      "Eleven Months of Perfection",
      "One Magical Year",
    ],
    57: [
      "Two Years of Joy",
      "Three Years of Magic",
      "Four Years of Fun",
      "Five Years of Happiness",
      "Six Years of Wonder",
      "Seven Years of Magic",
      "Eight Years of Smiles",
      "Nine Years of Joy",
    ],
    58: [
      "Față card: Dacă ai primit asta, știi ce ai de făcut. Verso card: Un gest mic care spune exact ce trebuie. Ahava Cakes",
      "Față card: Pentru tine. Verso card: Fără alte explicații. Ahava Cakes",
      "Față card: Un semn bun. Verso card: Foarte bun. Ahava Cakes",
      "Față card:Când vrei să spui ceva. Verso card: Fără să spui nimic.",
      "Față card: Știi tu de ce. Verso card: Ahava Cakes",
      "Față card: A fost o idee bună. Verso card: Ahava Cakes",
      "Față card: Exact ce trebuia. Verso card: Ahava Cakes",
      "Față card: Data viitoare, tu. Verso card: Ahava Cakes",
    ],
  };

  function requiresMessageDropdown(item) {
    return !!MESSAGE_OPTIONS_BY_PRODUCT_ID[item.id];
  }

  function getMessageOptions(item) {
    return MESSAGE_OPTIONS_BY_PRODUCT_ID[item.id] || [];
  }

  function getDiscountBadge(item) {
    const pret = Number(item.pret || 0);
    const reducereTip = item.reducere_tip || "procent";

    if (reducereTip === "procent") {
      return `-${Number(item.reducere_procent || 0)}%`;
    }

    const pretRedus = Number(item.pret_redus || 0);
    if (pret > 0 && pretRedus > 0 && pretRedus < pret) {
      const procent = Math.round(((pret - pretRedus) / pret) * 100);
      return `-${procent}%`;
    }

    return "";
  }

  const [editingItems, setEditingItems] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadingByCartId, setUploadingByCartId] = useState({});
  const itemRefs = useRef({});

  const hasUploadingImages = Object.values(uploadingByCartId).some(Boolean);

  const toggleEdit = (cartId) => {
    setEditingItems((prev) => ({
      ...prev,
      [cartId]: !prev[cartId],
    }));
  };

  const validateCartBeforeCheckout = () => {
    const nextErrors = {};
    let hasErrors = false;
    let firstErrorCartId = null;

    items.forEach((item) => {
      const itemErrors = {};
      const gramaje = buildGramajOptions(item);

      if (
        !isCustomCake(item) &&
        isVariablePrice(item) &&
        gramaje.length > 0 &&
        !item.gramaj
      ) {
        itemErrors.gramaj = "Te rugăm să alegi gramajul.";
        hasErrors = true;
      }

      if (requiresMessageDropdown(item) && !item.mesaj_selectat) {
        itemErrors.mesaj_selectat = "Te rugăm să alegi mesajul.";
        hasErrors = true;
      }

      if (Object.keys(itemErrors).length) {
        nextErrors[item.cartId] = itemErrors;

        if (!firstErrorCartId) {
          firstErrorCartId = item.cartId;
        }
      }
    });

    setFieldErrors(nextErrors);

    if (firstErrorCartId) {
      requestAnimationFrame(() => {
        itemRefs.current[firstErrorCartId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }

    return !hasErrors;
  };

  const total = useMemo(() => roundPrice(totalPret), [totalPret]);
  const isBelowMinimum = total < MIN_ORDER_VALUE;
  const remainingUntilMinimum = MIN_ORDER_VALUE - total;

  if (!items.length) {
    return (
      <Page>
        <Inner>
          <Title>Coșul meu</Title>
          <EmptyBox>
            Coșul este gol momentan.
            <br />
            <br />
            <SecondaryBtn type="button" onClick={() => navigate("/")}>
              Înapoi la magazin
            </SecondaryBtn>
          </EmptyBox>
        </Inner>
      </Page>
    );
  }

  return (
    <Page>
      <Inner>
        <Title>Coșul meu</Title>
        <Subtitle>
          Revizuiește produsele, completează opțiunile necesare și continuă
          către finalizarea comenzii.
        </Subtitle>

        <Grid>
          <List>
            {items.map((item) => {
              const gramaje = buildGramajOptions(item);
              const hasDiscount =
                !isCustomCake(item) &&
                ((item.reducere_tip === "procent" &&
                  Number(item.reducere_procent || 0) > 0) ||
                  (item.reducere_tip === "pret_nou" &&
                    Number(item.pret_redus || 0) > 0 &&
                    Number(item.pret_redus || 0) < Number(item.pret || 0)));
              const lineTotal = calcLineTotal(item);
              const oldLineTotal = calcLineTotalBeforeDiscount(item);
              const isEditing = !!editingItems[item.cartId];
              const customCake = isCustomCake(item);
              const bentoCake = isBentoCake(item);

              return (
                <ItemCard
                  key={item.cartId}
                  ref={(el) => {
                    itemRefs.current[item.cartId] = el;
                  }}
                  style={
                    fieldErrors[item.cartId]
                      ? {
                          border: "1px solid #d9534f",
                          boxShadow: "0 0 0 3px rgba(217, 83, 79, 0.12)",
                        }
                      : undefined
                  }
                >
                  {hasDiscount && (
                    <DiscountBadge>{getDiscountBadge(item)}</DiscountBadge>
                  )}

                  <ItemTop>
                    <Img src={item.imagine} alt={item.nume} />

                    <Info>
                      <NameRow>
                        <Name>{item.nume}</Name>

                        <HeaderTools>
                          <RemoveIconBtn
                            type="button"
                            onClick={() => removeFromCart(item.cartId)}
                            aria-label="Elimină produs"
                            title="Elimină produs"
                          >
                            <FiTrash2 />
                          </RemoveIconBtn>
                        </HeaderTools>
                      </NameRow>

                      {item.termen_ore === 0 ? (
                        <Meta>{formatDisponibilitate(item.termen_ore)}</Meta>
                      ) : (
                        <MetaAlert>
                          {formatDisponibilitate(item.termen_ore)}
                        </MetaAlert>
                      )}

                      {!isVariablePrice(item) && item.gramaj ? (
                        <Meta>Gramaj: {formatGramaj(item.gramaj)}</Meta>
                      ) : null}

                      {!customCake &&
                        isVariablePrice(item) &&
                        gramaje.length > 0 && (
                          <InlineControls>
                            <GramajSelect
                              value={item.gramaj || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                updateGramaj(
                                  item.cartId,
                                  value ? Number(value) : "",
                                );
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  [item.cartId]: {
                                    ...(prev[item.cartId] || {}),
                                    gramaj: "",
                                  },
                                }));
                              }}
                            >
                              <option value="">Alege gramajul</option>
                              {gramaje.map((g) => (
                                <option key={g} value={g}>
                                  {formatGramaj(g)}
                                </option>
                              ))}
                            </GramajSelect>
                          </InlineControls>
                        )}

                      {fieldErrors[item.cartId]?.gramaj ? (
                        <MetaAlert>{fieldErrors[item.cartId].gramaj}</MetaAlert>
                      ) : null}

                      {requiresMessageDropdown(item) && (
                        <InlineControls>
                          <SelectField
                            value={item.mesaj_selectat || ""}
                            onChange={(e) => {
                              updateMesajSelectat(item.cartId, e.target.value);
                              setFieldErrors((prev) => ({
                                ...prev,
                                [item.cartId]: {
                                  ...(prev[item.cartId] || {}),
                                  mesaj_selectat: "",
                                },
                              }));
                            }}
                          >
                            <option value="">Alege mesajul</option>
                            {getMessageOptions(item).map((msg) => (
                              <option key={msg} value={msg}>
                                {msg}
                              </option>
                            ))}
                          </SelectField>
                        </InlineControls>
                      )}

                      {fieldErrors[item.cartId]?.mesaj_selectat ? (
                        <MetaAlert>
                          {fieldErrors[item.cartId].mesaj_selectat}
                        </MetaAlert>
                      ) : null}

                      {item.mesaj_selectat ? (
                        <Meta>Mesaj ales: {item.mesaj_selectat}</Meta>
                      ) : null}

                      {customCake && (
                        <>
                          <CustomSummaryBox>
                            <SummaryLine>
                              <SummaryKey>Sortiment</SummaryKey>
                              <SummaryValue>
                                {item.personalizare_nume || "Necompletat"}
                              </SummaryValue>
                            </SummaryLine>

                            <SummaryLine>
                              <SummaryKey>Greutate</SummaryKey>
                              <SummaryValue>
                                {item.gramaj
                                  ? formatGramaj(item.gramaj)
                                  : "Necompletat"}
                              </SummaryValue>
                            </SummaryLine>

                            {!bentoCake && (
                              <SummaryLine>
                                <SummaryKey>Preț / kg</SummaryKey>
                                <SummaryValue>
                                  {item.personalizare_pret_kg
                                    ? `${formatLei(item.personalizare_pret_kg)} / kg`
                                    : "Necompletat"}
                                </SummaryValue>
                              </SummaryLine>
                            )}

                            {item.descriere_decor ? (
                              <SummaryLine>
                                <SummaryKey>Decor</SummaryKey>
                                <SummaryValue>
                                  {item.descriere_decor}
                                </SummaryValue>
                              </SummaryLine>
                            ) : null}

                            {item.mentiuni ? (
                              <SummaryLine>
                                <SummaryKey>Mențiuni</SummaryKey>
                                <SummaryValue>{item.mentiuni}</SummaryValue>
                              </SummaryLine>
                            ) : null}

                            {item.poza_model_url ? (
                              <SummaryLine>
                                <SummaryKey>Poză model</SummaryKey>
                                <SummaryValue>
                                  <FileHint
                                    href={item.poza_model_url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Vezi poza adăugată
                                  </FileHint>
                                </SummaryValue>
                              </SummaryLine>
                            ) : null}
                          </CustomSummaryBox>

                          <EditToggleBtn
                            type="button"
                            onClick={() => toggleEdit(item.cartId)}
                          >
                            {isEditing ? "Închide editarea" : "Editează tortul"}
                          </EditToggleBtn>

                          {isEditing && (
                            <>
                              <Divider />

                              <ExtraFields>
                                <FieldGroup>
                                  <FieldLabel>Personalizare</FieldLabel>
                                  <SelectField
                                    value={item.personalizare_nume || ""}
                                    onChange={(e) => {
                                      const selected =
                                        optiuniPersonalizare.find(
                                          (opt) => opt.nume === e.target.value,
                                        );

                                      updatePersonalizare(
                                        item.cartId,
                                        selected?.nume || "",
                                        selected?.pret_kg || 0,
                                      );
                                    }}
                                  >
                                    <option value="">
                                      Alege personalizarea
                                    </option>
                                    {optiuniPersonalizare.map((opt) => (
                                      <option key={opt.nume} value={opt.nume}>
                                        {opt.nume} ({formatLei(opt.pret_kg)} /
                                        kg)
                                      </option>
                                    ))}
                                  </SelectField>
                                </FieldGroup>

                                {gramaje.length > 0 && (
                                  <FieldGroup>
                                    <FieldLabel>Greutate</FieldLabel>
                                    <GramajSelect
                                      value={item.gramaj || ""}
                                      onChange={(e) =>
                                        updateGramaj(
                                          item.cartId,
                                          e.target.value
                                            ? Number(e.target.value)
                                            : "",
                                        )
                                      }
                                    >
                                      <option value="">Alege gramajul</option>
                                      {gramaje.map((g) => (
                                        <option key={g} value={g}>
                                          {formatGramaj(g)}
                                        </option>
                                      ))}
                                    </GramajSelect>
                                  </FieldGroup>
                                )}

                                <FieldGroupWide>
                                  <FieldLabel>Decor dorit</FieldLabel>
                                  <TextArea
                                    value={item.descriere_decor || ""}
                                    onChange={(e) =>
                                      updateDescriereDecor(
                                        item.cartId,
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Descrie cum vrei să arate tortul"
                                  />
                                </FieldGroupWide>

                                <FieldGroupWide>
                                  <FieldLabel>Mențiuni</FieldLabel>
                                  <TextArea
                                    value={item.mentiuni || ""}
                                    onChange={(e) =>
                                      updateMentiuni(
                                        item.cartId,
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Ex: mesaj pe tort, culori dorite, alte detalii importante"
                                  />
                                </FieldGroupWide>

                                <FieldGroupWide>
                                  <FieldLabel>Poză model</FieldLabel>

                                  <HiddenFileInput
                                    id={`poza-model-${item.cartId}`}
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;

                                      try {
                                        setUploadingByCartId((prev) => ({
                                          ...prev,
                                          [item.cartId]: true,
                                        }));

                                        const publicUrl =
                                          await uploadCustomCakeImage(file);
                                        updatePozaModel(item.cartId, publicUrl);
                                      } catch (err) {
                                        console.error(
                                          "Upload poză model eșuat:",
                                          err,
                                        );
                                        alert(
                                          err?.message ||
                                            "Nu am putut încărca poza model.",
                                        );
                                      } finally {
                                        setUploadingByCartId((prev) => ({
                                          ...prev,
                                          [item.cartId]: false,
                                        }));
                                        e.target.value = "";
                                      }
                                    }}
                                  />

                                  <FileUploadRow>
                                    <FileTrigger
                                      htmlFor={`poza-model-${item.cartId}`}
                                    >
                                      {uploadingByCartId[item.cartId]
                                        ? "Se încarcă..."
                                        : "Adaugă poză"}
                                    </FileTrigger>

                                    <FileName>
                                      {uploadingByCartId[item.cartId]
                                        ? "Se încarcă..."
                                        : item.poza_model_url
                                          ? "Poză încărcată"
                                          : "Nicio poză selectată"}
                                    </FileName>
                                  </FileUploadRow>

                                  {item.poza_model_url ? (
                                    <>
                                      <FileHint
                                        href={item.poza_model_url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Vezi poza adăugată
                                      </FileHint>

                                      <div style={{ marginTop: "10px" }}>
                                        <img
                                          src={item.poza_model_url}
                                          alt="Poză model tort"
                                          style={{
                                            width: "120px",
                                            height: "120px",
                                            objectFit: "cover",
                                            borderRadius: "12px",
                                            border:
                                              "1px solid rgba(0,0,0,0.08)",
                                          }}
                                        />
                                      </div>
                                    </>
                                  ) : (
                                    <FileHint as="span">
                                      Poți adăuga o imagine model pentru tort.
                                    </FileHint>
                                  )}
                                </FieldGroupWide>
                              </ExtraFields>
                            </>
                          )}
                        </>
                      )}
                    </Info>
                  </ItemTop>

                  <Actions $isCustomCake={customCake}>
                    <TopActions>
                      <RemoveIconBtn
                        type="button"
                        onClick={() => removeFromCart(item.cartId)}
                        aria-label="Elimină produs"
                        title="Elimină produs"
                      >
                        <FiTrash2 />
                      </RemoveIconBtn>

                      <QtyWrap>
                        <QtyBtn
                          type="button"
                          onClick={() =>
                            updateQuantity(item.cartId, item.cantitate - 1)
                          }
                        >
                          -
                        </QtyBtn>

                        <QtyValue>{item.cantitate}</QtyValue>

                        <QtyBtn
                          type="button"
                          onClick={() =>
                            updateQuantity(item.cartId, item.cantitate + 1)
                          }
                        >
                          +
                        </QtyBtn>
                      </QtyWrap>
                    </TopActions>

                    <BottomRow>
                      <MobileQtyWrap>
                        <QtyBtn
                          type="button"
                          onClick={() =>
                            updateQuantity(item.cartId, item.cantitate - 1)
                          }
                        >
                          -
                        </QtyBtn>

                        <QtyValue>{item.cantitate}</QtyValue>

                        <QtyBtn
                          type="button"
                          onClick={() =>
                            updateQuantity(item.cartId, item.cantitate + 1)
                          }
                        >
                          +
                        </QtyBtn>
                      </MobileQtyWrap>

                      <PriceWrap>
                        {hasDiscount && (
                          <OldPrice>{formatLei(oldLineTotal)}</OldPrice>
                        )}
                        <Price>{formatLei(lineTotal)}</Price>
                      </PriceWrap>
                    </BottomRow>
                  </Actions>
                </ItemCard>
              );
            })}
          </List>

          <Summary>
            <SummaryTitle>Sumar comandă</SummaryTitle>
            <SummaryText>
              Totul este pregătit. Verifică totalul și continuă către
              finalizarea comenzii.
            </SummaryText>

            <SummaryCard>
              <SummaryRow>
                <span>Total</span>
                <strong>{formatLei(total)}</strong>
              </SummaryRow>

              <SummaryMini>
                {items.length} {items.length === 1 ? "produs" : "produse"} în
                coș
              </SummaryMini>
            </SummaryCard>

            {isBelowMinimum && (
              <SummaryMini style={{ color: "#b42318", marginTop: "10px" }}>
                Comanda minimă este de {MIN_ORDER_VALUE} lei. Mai trebuie să
                adaugi produse de {formatLei(remainingUntilMinimum)}.
              </SummaryMini>
            )}

            <PrimaryBtn
              type="button"
              disabled={hasUploadingImages || isBelowMinimum}
              onClick={() => {
                if (isBelowMinimum) {
                  alert(
                    `Valoarea minimă a comenzii este de ${MIN_ORDER_VALUE} lei. Mai adaugă produse de încă ${remainingUntilMinimum} lei.`,
                  );
                  return;
                }

                const isValid = validateCartBeforeCheckout();
                if (!isValid) return;

                navigate("/plasare-comanda");
              }}
            >
              {hasUploadingImages
                ? "Se încarcă imagini..."
                : isBelowMinimum
                  ? `Minim ${MIN_ORDER_VALUE} lei`
                  : "Continuă către comandă"}
            </PrimaryBtn>

            <SecondaryBtn type="button" onClick={() => navigate("/")}>
              Continuă cumpărăturile
            </SecondaryBtn>
          </Summary>
        </Grid>
      </Inner>
    </Page>
  );
}
